import { useTranslation } from "react-i18next";

import {
  ANALYSIS_PROVIDER_NAMES,
  ANALYSIS_RPOVIDER_DISPLAY_NAMES,
  IntegrationsRow
} from "../../../Pages/Integrations/types";
import { COLUMN_NAMES, COLUMN_RESOURCES, useDateTime } from "../../../shared";
import DateTime from "../../../shared/components/dateTime/DateTime";
import {
  generateTranslatedText,
  parseResource
} from "../../../shared/functions/functions";
import useCustomResources from "../../../shared/hooks/useCustomResources/useCustomResources";
import SchedulesList from "../../Schedules/components/SchedulesList/SchedulesList";
import VehicleDataTypesList from "../../Schedules/components/VehicleDataTypesList/VehicleDataTypesList";
import SharedStatusBadge from "../../SharedStatusBadge/SharedStatusBadge";
import { TOGGLE_SWITCH_SIZES, ToggleSwitch } from "../../ToggleSwitch";
import DriverLastActivityState from "../components/DriverLastActivityState/DriverLastActivityState";
import MultiValuesCellItem from "../components/MultiValuesCellItem/MultiValuesCellItem";
import SharedLastDownloadBadge from "../components/SharedLastDownloadBadge/SharedLastDownloadBadge";

const useTableFunctions = (
  onToggleChange?: (rowData: IntegrationsRow) => Promise<void>
) => {
  const {
    renderLastDownloadAttemptBadgeWithTooltip,
    renderNextDownloadAttemptText,
    renderMethodText,
    renderTextOrNotAvailable,
    renderImeiOrTooltip
  } = useCustomResources();

  const { t, i18n } = useTranslation();
  const { currentTimeFormatHoursMinutes, currentDateFormatWithoutTime } =
    useDateTime();

  const renderStringResource = (
    name: COLUMN_NAMES,
    value: string | number | null
  ) => {
    const dateTimeValues = [
      COLUMN_NAMES.SeenAt,
      COLUMN_NAMES.CreatedAt,
      COLUMN_NAMES.LastConnection,
      COLUMN_NAMES.UpdatedAt,
      COLUMN_NAMES.DownloadTime,
      COLUMN_NAMES.ActivityPeriod,
      COLUMN_NAMES.InvitedAt,
      COLUMN_NAMES.LastEvent,
      COLUMN_NAMES.ExpirationDate
    ];

    if (value === null || value === "") {
      return "-";
    }

    if (dateTimeValues.includes(name)) {
      const showWithoutTime =
        name === COLUMN_NAMES.ActivityPeriod ||
        name === COLUMN_NAMES.ExpirationDate;

      return (
        <DateTime
          date={String(value)}
          format={
            showWithoutTime
              ? currentDateFormatWithoutTime
              : currentTimeFormatHoursMinutes
          }
        />
      );
    }

    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return <>{value}</>;
    }

    return value;
  };

  const renderUniqueColumns = (resource: COLUMN_RESOURCES, data: any) => {
    switch (resource) {
      case COLUMN_RESOURCES.NextDownload:
        return <>{renderNextDownloadAttemptText(data.nextDownload, true)}</>;

      case COLUMN_RESOURCES.LastDownload:
        return renderLastDownloadAttemptBadgeWithTooltip(data);

      case COLUMN_RESOURCES.Status:
        if (data.status === null) {
          return "-";
        }

        return (
          <SharedStatusBadge status={data.status} isDisabled={data?.disabled} />
        );

      case COLUMN_RESOURCES.LastActivity:
        return <DriverLastActivityState lastActivity={data.lastActivity} />;

      case COLUMN_RESOURCES.Type:
        return <>{renderMethodText(data.type)}</>;

      case COLUMN_RESOURCES.InvitationStatus:
        return (
          <SharedStatusBadge
            status={data.invitationStatus}
            customTooltipText=""
          />
        );

      case COLUMN_RESOURCES.Imei:
        return renderImeiOrTooltip(data.imei);

      case COLUMN_RESOURCES.VehicleImei:
        return <>{renderTextOrNotAvailable(data.vehicleImei)}</>;

      case COLUMN_RESOURCES.Role:
        return <>{t(`General##${data.role}`)}</>;

      case COLUMN_RESOURCES.SourceName:
        return <>{generateTranslatedText(data.sourceName, i18n, t)}</>;

      case COLUMN_RESOURCES.CreditStatus:
        return <>{t(`General##${data.creditStatus}`)}</>;

      case COLUMN_RESOURCES.Provider: {
        const displayedString =
          data?.provider === ANALYSIS_PROVIDER_NAMES.VDO
            ? ANALYSIS_RPOVIDER_DISPLAY_NAMES.VDO
            : data?.provider;

        return <>{displayedString}</>;
      }

      default:
        return null;
    }
  };

  const renderCustomColumns = (resource: COLUMN_RESOURCES, data: any) => {
    if (resource === COLUMN_RESOURCES.Enabled && onToggleChange) {
      return (
        <ToggleSwitch
          preventParentOnClick
          size={TOGGLE_SWITCH_SIZES.Medium}
          checked={Boolean(data.enabled)}
          onChange={_ => onToggleChange(data)}
          justifyContent="left"
        />
      );
    }

    if (resource === COLUMN_RESOURCES.LastSuccessfulDownloadTime) {
      const lastDownloadTime = data.lastSuccessfulDownloadTime;
      return lastDownloadTime ? (
        <SharedLastDownloadBadge
          lastDownloadDate={lastDownloadTime}
          isDisabled={data?.disabled}
        />
      ) : (
        <>-</>
      );
    }

    if (resource === COLUMN_RESOURCES.SchedulesList) {
      const schedulesParams = {
        id: data.id,
        schedules: data[resource],
        status: data.status
      };
      return (
        <SchedulesList
          schedulesParams={schedulesParams}
          scheduleSummaries={data.scheduleSummaries}
        />
      );
    }

    if (resource === COLUMN_RESOURCES.FileTypes) {
      return (
        data[resource] && <VehicleDataTypesList schedule={data[resource]} />
      );
    }

    return null;
  };

  const renderValue = (
    name: COLUMN_NAMES,
    resource: COLUMN_RESOURCES,
    data: any,
    secondCellValue?: COLUMN_RESOURCES
  ) => {
    if (secondCellValue) {
      const firstParsedValue = parseResource(resource, data);
      const secondParsedValue = parseResource(secondCellValue, data);

      return (
        <>
          <MultiValuesCellItem
            title={t("General##start")}
            value={renderStringResource(name, firstParsedValue)}
          />
          <MultiValuesCellItem
            title={t("General##end")}
            value={renderStringResource(name, secondParsedValue)}
          />
        </>
      );
    }

    const uniqueColumns = renderUniqueColumns(resource, data);
    if (uniqueColumns !== null) {
      return uniqueColumns;
    }

    const customColumns = renderCustomColumns(resource, data);
    if (customColumns !== null) {
      return customColumns;
    }

    const parsedValue = parseResource(resource, data);

    if (
      typeof parsedValue === "string" ||
      typeof parsedValue === "number" ||
      parsedValue === null
    ) {
      return renderStringResource(name, parsedValue);
    }

    return null;
  };

  return { renderValue, renderStringResource };
};

export default useTableFunctions;
